import { grey } from '@ant-design/colors'
import { faPalletBox, faSave } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Divider, notification, Popconfirm, Skeleton } from 'antd'
import { useContext, useEffect, useReducer, useState } from 'react'
import { AuthenticationContext } from '../AuthenticationContext'
import HeroButton from '../components/common/HeroButton'
import HeroButtonsContainer from '../components/common/HeroButtonsContainer'
import HeroContainer from '../components/common/HeroContainer'
import VSpace from '../components/common/VSpace'
import VendorsForm from '../components/Vendors/VendorsForm'
import { emptyVendor, hasVendorChanged } from '../components/Vendors/vendorsModel'
import { useRemoveVendorMutation, useSaveVendorMutation, useVendor } from '../components/Vendors/vendorsQueries'
import VendorsSidebar from '../components/Vendors/VendorsSidebar'
import { NavigationService } from '../services/navigationService'
import { reducer } from '../components/Vendors/vendorsReducer'

type Props = {
  showDeletedView: boolean
  navigationService: NavigationService
}

const NEW_VENDOR_ID = 0

function VendorsPage({ showDeletedView, navigationService }: Props) {
  const [vendorState, dispatch] = useReducer(reducer, emptyVendor());

  const { currentUser } = useContext(AuthenticationContext)
  const [selectedVendorId, setSelectedVendorId] = useState<number | undefined>()

  const vendorQuery = useVendor(selectedVendorId)
  const heroImage = currentUser?.tenant.publicFolderUrl + '/images/default-item.jpg'

  const removeVendorMutation = useRemoveVendorMutation(() => {
    setSelectedVendorId(undefined)
    dispatch({ type: 'VENDOR_SELECTED', vendor: emptyVendor() })
  })

  const upsertVendorMutation = useSaveVendorMutation((vendor) => {
    setSelectedVendorId(vendor.id)
  })

  const saveEnabled = !upsertVendorMutation.isLoading && hasVendorChanged(vendorQuery.data, vendorState, selectedVendorId)

  const confirmDiscardChanges = () => {
    if (!saveEnabled) {
      return true
    }
    return confirm('The current item has changes. Do you want to discard them?')
  }

  const handleCreate = () => {
    if (!confirmDiscardChanges()) {
      return
    }
    dispatch({
      type: 'VENDOR_SELECTED', vendor: {
        ...emptyVendor(),
        name: 'New Vendor',
        id: NEW_VENDOR_ID
      }
    })
    setSelectedVendorId(NEW_VENDOR_ID)
    navigationService.setHash(`new`)
  }

  const handleSelect = (vendorId: number) => {
    if (!confirmDiscardChanges()) {
      return
    }

    setSelectedVendorId(vendorId)
  }

  const saveChanges = () => {
    upsertVendorMutation.mutate(
      { vendor: vendorState },
      { onSuccess: () => notification.success({ message: 'Successfully updated this vendor' }) }
    )
  }

  useEffect(() => {
    if (vendorQuery.data) {
      dispatch({ type: 'VENDOR_SELECTED', vendor: vendorQuery.data })
    }
  }, [vendorQuery.data])

  return <>
    <VendorsSidebar
      showDeletedView={showDeletedView}
      navigationService={navigationService}
      onCreate={handleCreate}
      onVendorSelect={handleSelect}
      selectedVendorId={selectedVendorId}
    />
    <div id="main" className="has-elements fluid-container">
      {selectedVendorId !== undefined && <>
        <HeroContainer imgSrc={heroImage} />
        <div className="hero-details">
          <h1>{vendorState.name}</h1>
          <HeroButtonsContainer>
            <HeroButton icon={faSave}
              onClick={saveChanges}
              disabled={!saveEnabled}
            />
          </HeroButtonsContainer>
        </div>
        <section>
          <VSpace>
            {vendorQuery.isLoading && <Skeleton active />}
            {(!vendorQuery.isLoading && vendorState) && <>
              <VendorsForm
                vendor={vendorState}
                dispatch={dispatch}
              />
              {vendorState.id > 0 && <>
                <Divider />
                <Popconfirm onConfirm={() => removeVendorMutation.mutate(vendorState.id)} title={`Are you sure you want to remove vendor ${vendorState.name}`}>
                  <Button danger type="primary">Remove Vendor</Button>
                </Popconfirm>
              </>}
            </>
            }
          </VSpace>
        </section>
      </>}
      {selectedVendorId === undefined &&
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100%',
          color: grey[2],
        }}>
          <FontAwesomeIcon icon={faPalletBox} size="6x" />
          <h1 style={{ fontFamily: 'Proza Libre, sans-serif' }}>Select a Vendor</h1>
        </div>
      }
    </div>
  </>
}

export default VendorsPage